import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  SUBSCRIPTION_FILTERS,
  type SortDir,
  type SubscriptionFilter,
  type UsersSort,
} from '@/lib/adminApi';
import { useSearchUsers } from '@/lib/useAdminUsers';
import { useMe } from '@/lib/useMe';
import { OnboardingTestAccountDialog } from '@/components/OnboardingTestAccountDialog';

/** Search box triggers a query only past this many characters, unless the
 * typed value already looks like a full uuid (a paste, not a name/email
 * fragment) — matching the build brief's "debounced, min 3 chars or a full
 * id" rule. */
const MIN_QUERY_LENGTH = 3;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DEBOUNCE_MS = 300;
const PAGE_LIMIT = 20;

/** The `?subscription=` control (ClickUp 869f48zh7). Three sets with labels
 * that say WHICH fact each one is, because "has a subscription" is two
 * different facts and Support means the first: "Paying now" is a live
 * Solidgate subscription; "On a paid plan" is organizations.plan_id off the
 * default — which includes plans this console activated by hand and gifts.
 * One "by plan" filter would show Support gifted plans as customers. */
const SUBSCRIPTION_OPTIONS: ReadonlyArray<{ value: SubscriptionFilter | ''; label: string }> = [
  { value: '', label: 'All users' },
  { value: 'paying', label: 'Paying now' },
  { value: 'paid_plan', label: 'On a paid plan' },
  { value: 'free', label: 'Free / none' },
];

/** 2066 -> "2 066": a thin space between thousands, the way the owner reads
 * the numbers ("235 of 2 066 users"). */
function formatCount(n: number): string {
  return n.toLocaleString('en-US').replace(/,/g, '\u2009');
}

function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

function parseSubscription(raw: string | null): SubscriptionFilter | undefined {
  return (SUBSCRIPTION_FILTERS as readonly string[]).includes(raw ?? '')
    ? (raw as SubscriptionFilter)
    : undefined;
}

/** Credits come with at most two decimals, thousands in the same thin space. */
function formatCredits(n: number): string {
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 }).replace(/,/g, ' ');
}

/** The sortable activity columns. `last_generation` is the older one; the
 * others came with ClickUp 1amz0xe2n53. `desc`/`asc` are the words the title
 * uses for the two directions of this column's values. */
interface SortColumn {
  sort: Exclude<UsersSort, 'created'>;
  name: string;
  label: string;
  meaning: string;
  descWord: string;
  ascWord: string;
  numeric: boolean;
}

const SORT_COLUMNS: Record<'last_generation' | 'messages' | 'content' | 'credits_spent' | 'last_active', SortColumn> =
  {
    messages: {
      sort: 'messages',
      name: 'sort-messages',
      label: 'Messages',
      meaning: 'Messages sent to the chat agent, all time',
      descWord: 'highest first',
      ascWord: 'lowest first',
      numeric: true,
    },
    content: {
      sort: 'content',
      name: 'sort-content',
      label: 'Generations',
      meaning: 'Successful charged generations (images, videos, texts), all time, net of refunds',
      descWord: 'highest first',
      ascWord: 'lowest first',
      numeric: true,
    },
    credits_spent: {
      sort: 'credits_spent',
      name: 'sort-credits-spent',
      label: 'Credits spent',
      meaning: 'Net credits this user spent on agent and generations, all time',
      descWord: 'highest first',
      ascWord: 'lowest first',
      numeric: true,
    },
    last_active: {
      sort: 'last_active',
      name: 'sort-last-active',
      label: 'Last active',
      meaning: 'Latest of sign-in, chat activity, generation or sign-up',
      descWord: 'newest first',
      ascWord: 'oldest first',
      numeric: false,
    },
    last_generation: {
      sort: 'last_generation',
      name: 'sort-last-generation',
      label: 'Last generation',
      meaning: 'Newest generation of this user',
      descWord: 'newest first',
      ascWord: 'oldest first',
      numeric: false,
    },
  };

function parseSort(raw: string | null): SortColumn['sort'] | undefined {
  return raw !== null && Object.prototype.hasOwnProperty.call(SORT_COLUMNS, raw)
    ? (raw as SortColumn['sort'])
    : undefined;
}

function parseDir(raw: string | null): SortDir {
  return raw === 'asc' ? 'asc' : 'desc';
}

type ActiveMode = '7d' | '30d' | 'inactive' | 'range';
const ACTIVE_MODES: readonly ActiveMode[] = ['7d', '30d', 'inactive', 'range'];

const ACTIVE_OPTIONS: ReadonlyArray<{ value: ActiveMode | ''; label: string }> = [
  { value: '', label: 'Any time' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'inactive', label: 'Inactive for more than…' },
  { value: 'range', label: 'Date range' },
];

const DEFAULT_INACTIVE_DAYS = 14;
const MAX_DAYS = 3650;

function parseActive(raw: string | null): ActiveMode | undefined {
  return (ACTIVE_MODES as readonly string[]).includes(raw ?? '') ? (raw as ActiveMode) : undefined;
}

/** A whole number of days, 1..3650; anything else is ignored. */
function parseDays(raw: string | null): number | undefined {
  if (raw === null || !/^\d+$/.test(raw)) return undefined;
  const n = Number(raw);
  return n >= 1 && n <= MAX_DAYS ? n : undefined;
}

/** A YYYY-MM-DD that is a real calendar date, else undefined. */
function parseDateParam(raw: string | null): string | undefined {
  const m = raw === null ? null : /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!m) return undefined;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(y, mo - 1, d);
  return date.getFullYear() === y && date.getMonth() === mo - 1 && date.getDate() === d
    ? (raw as string)
    : undefined;
}

/** Local midnight of the date, plus `addDays`, as an ISO instant. The day
 * AFTER the "to" date makes the API's exclusive upper bound include that whole
 * day. */
function localMidnightIso(date: string, addDays: number): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, m - 1, d + addDays).toISOString();
}

/** A non-negative finite number, else undefined (raw text kept by the URL). */
function parseCredits(raw: string | null): number | undefined {
  if (raw === null || raw.trim() === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

function sanitizeDaysText(raw: string): string {
  return parseDays(raw) === undefined ? '' : raw;
}

function sanitizeCreditsText(raw: string): string {
  return parseCredits(raw) === undefined ? '' : raw.trim();
}

/** A number input kept as local text and written to the URL only after the
 * debounce, so typing does not fire a query per keystroke. The URL stays the
 * source of truth for the request; the text is just what the person is
 * typing. Writes only when the debounced text changes, never on mount. When
 * the URL changes from outside (a sidebar link to /users, a forwarded link
 * opened in the tab — same route, no remount) the text follows it. `snap`
 * replaces text that did not apply (0 days, -3 credits) with what did. */
function useDebouncedUrlField(
  urlValue: string,
  sanitize: (raw: string) => string,
  write: (value: string) => void,
) {
  const [text, setText] = useState(urlValue);
  const debounced = useDebouncedValue(text, DEBOUNCE_MS);
  const lastWritten = useRef(text);
  // What the URL should hold after our own latest write: tells our write
  // coming back apart from an outside change.
  const ownWrite = useRef(urlValue);
  const prevUrl = useRef(urlValue);
  const writeRef = useRef(write);
  writeRef.current = write;
  useEffect(() => {
    if (debounced === lastWritten.current) return;
    lastWritten.current = debounced;
    ownWrite.current = sanitize(debounced);
    writeRef.current(ownWrite.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `sanitize` is a module-level function
  }, [debounced]);
  useEffect(() => {
    if (urlValue === prevUrl.current) return;
    prevUrl.current = urlValue;
    if (urlValue === ownWrite.current) return;
    ownWrite.current = urlValue;
    lastWritten.current = urlValue;
    setText(urlValue);
  }, [urlValue]);
  const snap = () => setText((t) => sanitize(t));
  return [text, setText, snap] as const;
}

function activityFilterLabel(
  active: ActiveMode | undefined,
  inactiveDays: number,
  from: string | undefined,
  to: string | undefined,
): string | undefined {
  if (active === '7d') return 'Last 7 days';
  if (active === '30d') return 'Last 30 days';
  if (active === 'inactive') return `Inactive ${inactiveDays}+ days`;
  if (active !== 'range') return undefined;
  if (from && to) return `Active ${from} – ${to}`;
  if (from) return `Active from ${from}`;
  if (to) return `Active until ${to}`;
  return undefined;
}

function creditsFilterLabel(min: number | undefined, max: number | undefined): string | undefined {
  if (min !== undefined && max !== undefined) {
    return `Credits spent ${formatCredits(min)}–${formatCredits(max)}`;
  }
  if (min !== undefined) return `Credits spent ≥ ${formatCredits(min)}`;
  if (max !== undefined) return `Credits spent ≤ ${formatCredits(max)}`;
  return undefined;
}

/** A sortable column header: click cycles desc -> asc -> default. */
function SortHeader({
  column,
  sort,
  dir,
  onCycle,
}: {
  column: SortColumn;
  sort: SortColumn['sort'] | undefined;
  dir: SortDir;
  onCycle: (sort: SortColumn['sort']) => void;
}) {
  const active = sort === column.sort;
  const title = !active
    ? `${column.meaning}. Click to sort ${column.descWord}.`
    : dir === 'desc'
      ? `${column.meaning}. Sorted ${column.descWord} — click for ${column.ascWord}.`
      : `${column.meaning}. Sorted ${column.ascWord} — click to return to newest sign-up first.`;
  // The visible text is just the name and an arrow; the label says the state
  // and the next action (the th's aria-sort carries the state itself).
  const ariaLabel = !active
    ? `${column.label}. Click to sort ${column.descWord}`
    : dir === 'desc'
      ? `${column.label}, sorted ${column.descWord}. Click for ${column.ascWord}`
      : `${column.label}, sorted ${column.ascWord}. Click to remove the sort`;
  return (
    <th
      className={`px-3 py-2 ${column.numeric ? 'text-right' : ''}`}
      aria-sort={active ? (dir === 'desc' ? 'descending' : 'ascending') : undefined}
    >
      <button
        type="button"
        name={column.name}
        onClick={() => onCycle(column.sort)}
        aria-label={ariaLabel}
        title={title}
        className="font-medium hover:underline"
      >
        {column.label}
        {active ? (dir === 'desc' ? ' ↓' : ' ↑') : ''}
      </button>
    </th>
  );
}

const FIELD_CLASS =
  'rounded-card border border-border bg-surface-field px-3 py-2 text-body text-fg-primary placeholder:text-fg-placeholder';

export function UsersPage() {
  const [input, setInput] = useState('');
  const [page, setPage] = useState(1);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  // Dev-only tool: the API reports it per environment, so on prod the button
  // never renders (absent capability = off, an older API).
  const { data: me } = useMe();
  const canGenerateOnboardingAccount = me?.capabilities?.onboarding_test_accounts === true;
  const navigate = useNavigate();
  // Filter and sort live in the URL, not in component state, so a support
  // agent can forward "/users?subscription=paying&sort=last_generation" and
  // the recipient lands on the same list.
  const [searchParams, setSearchParams] = useSearchParams();
  const subscription = parseSubscription(searchParams.get('subscription'));
  const sort = parseSort(searchParams.get('sort'));
  const dir = parseDir(searchParams.get('dir'));
  const active = parseActive(searchParams.get('active'));
  const inactiveDays = parseDays(searchParams.get('inactive_days')) ?? DEFAULT_INACTIVE_DAYS;
  const rawFrom = parseDateParam(searchParams.get('active_from'));
  const rawTo = parseDateParam(searchParams.get('active_to'));
  // A hand-edited link with From after To means the same range read the
  // other way round; YYYY-MM-DD strings order like the dates.
  const [activeFrom, activeTo] = rawFrom && rawTo && rawFrom > rawTo ? [rawTo, rawFrom] : [rawFrom, rawTo];
  const creditsMinRaw = searchParams.get('credits_min');
  const creditsMaxRaw = searchParams.get('credits_max');
  const creditsMin = parseCredits(creditsMinRaw);
  const creditsMax = parseCredits(creditsMaxRaw);

  const debounced = useDebouncedValue(input, DEBOUNCE_MS);
  const trimmed = debounced.trim();
  const isFullId = UUID_RE.test(trimmed);
  const query = trimmed.length >= MIN_QUERY_LENGTH || isFullId ? trimmed : '';

  // The presets go to the server as day counts, not timestamps: a "now"
  // computed here would change on every render and the query key with it.
  // The date range is the one place timestamps are sent, and they are
  // derived from the URL's dates, so they are stable too.
  const { data, isLoading, isError } = useSearchUsers({
    q: query,
    page,
    limit: PAGE_LIMIT,
    subscription,
    sort,
    dir: sort ? dir : undefined,
    active_within_days: active === '7d' ? 7 : active === '30d' ? 30 : undefined,
    inactive_days: active === 'inactive' ? inactiveDays : undefined,
    active_from: active === 'range' && activeFrom ? localMidnightIso(activeFrom, 0) : undefined,
    active_to: active === 'range' && activeTo ? localMidnightIso(activeTo, 1) : undefined,
    credits_min: creditsMin,
    credits_max: creditsMax,
  });

  function handleInput(next: string) {
    setInput(next);
    setPage(1);
  }

  // react-router's functional setSearchParams reads the params of the render
  // it was created in, so two writes in one tick (two debounced fields firing
  // together) would overwrite each other. Build from the latest write instead.
  const latestParams = useRef(searchParams);
  latestParams.current = searchParams;

  function setUrlParams(changes: Record<string, string>) {
    const next = new URLSearchParams(latestParams.current);
    for (const [name, value] of Object.entries(changes)) {
      if (value) next.set(name, value);
      else next.delete(name);
    }
    latestParams.current = next;
    setSearchParams(next, { replace: true });
    setPage(1);
  }

  function setUrlParam(name: string, value: string) {
    setUrlParams({ [name]: value });
  }

  /** desc -> asc -> default (no `sort`, newest sign-up first). */
  function cycleSort(column: SortColumn['sort']) {
    if (sort !== column) setUrlParams({ sort: column, dir: '' });
    else if (dir === 'desc') setUrlParams({ sort: column, dir: 'asc' });
    else setUrlParams({ sort: '', dir: '' });
  }

  const [inactiveText, setInactiveText, snapInactive] = useDebouncedUrlField(
    searchParams.get('inactive_days') ?? '',
    sanitizeDaysText,
    (v) => setUrlParam('inactive_days', v),
  );
  const [creditsMinText, setCreditsMinText, snapCreditsMin] = useDebouncedUrlField(
    creditsMinRaw ?? '',
    sanitizeCreditsText,
    (v) => setUrlParam('credits_min', v),
  );
  const [creditsMaxText, setCreditsMaxText, snapCreditsMax] = useDebouncedUrlField(
    creditsMaxRaw ?? '',
    sanitizeCreditsText,
    (v) => setUrlParam('credits_max', v),
  );
  const users = data?.users ?? [];
  const total = data?.total ?? 0;
  const totalUnfiltered = data?.total_unfiltered ?? total;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));
  const subscriptionLabel = SUBSCRIPTION_OPTIONS.find((o) => o.value === (subscription ?? ''))?.label;
  const activityLabel = activityFilterLabel(active, inactiveDays, activeFrom, activeTo);
  const creditsLabel = creditsFilterLabel(creditsMin, creditsMax);
  const filterLabels = [
    subscription ? subscriptionLabel : undefined,
    activityLabel,
    creditsLabel,
  ].filter((l): l is string => Boolean(l));
  const filtered = filterLabels.length > 0;

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-h2 font-semibold text-fg-primary">Users</h1>
          <p className="mt-1 text-body text-fg-secondary">
            Search by email, name, or a full user id.
          </p>
        </div>
        {canGenerateOnboardingAccount && (
          <button
            type="button"
            onClick={() => setOnboardingOpen(true)}
            className="rounded-card border border-border px-4 py-2 text-body font-medium text-fg-primary hover:bg-state-hover"
          >
            Onboarding test account
          </button>
        )}
      </div>
      {onboardingOpen && canGenerateOnboardingAccount && (
        <OnboardingTestAccountDialog onClose={() => setOnboardingOpen(false)} />
      )}

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <input
          type="text"
          value={input}
          onChange={(e) => handleInput(e.target.value)}
          placeholder="Search email, name, or user id…"
          className="w-full max-w-md rounded-card border border-border bg-surface-field px-3 py-2 text-body text-fg-primary placeholder:text-fg-placeholder"
        />
        <label className="flex flex-col gap-1 text-meta text-fg-secondary">
          Subscription
          <select
            name="subscription"
            value={subscription ?? ''}
            onChange={(e) => setUrlParam('subscription', e.target.value)}
            className="rounded-card border border-border bg-surface-field px-3 py-2 text-body text-fg-primary"
          >
            {SUBSCRIPTION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-meta text-fg-secondary">
          Last active
          <select
            name="active"
            value={active ?? ''}
            onChange={(e) =>
              // One write: the new mode and none of the other modes' params.
              setUrlParams({
                active: e.target.value,
                inactive_days: '',
                active_from: '',
                active_to: '',
              })
            }
            className="rounded-card border border-border bg-surface-field px-3 py-2 text-body text-fg-primary"
          >
            {ACTIVE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        {active === 'inactive' && (
          <label className="flex flex-col gap-1 text-meta text-fg-secondary">
            days
            <input
              type="number"
              name="inactive_days"
              min={1}
              max={MAX_DAYS}
              step={1}
              value={inactiveText}
              onChange={(e) => setInactiveText(e.target.value)}
              onBlur={snapInactive}
              placeholder={String(DEFAULT_INACTIVE_DAYS)}
              className={`w-24 ${FIELD_CLASS}`}
            />
          </label>
        )}
        {active === 'range' && (
          <>
            <label className="flex flex-col gap-1 text-meta text-fg-secondary">
              From
              <input
                type="date"
                name="active_from"
                value={activeFrom ?? ''}
                onChange={(e) => setUrlParam('active_from', e.target.value)}
                className={FIELD_CLASS}
              />
            </label>
            <label className="flex flex-col gap-1 text-meta text-fg-secondary">
              To
              <input
                type="date"
                name="active_to"
                value={activeTo ?? ''}
                onChange={(e) => setUrlParam('active_to', e.target.value)}
                className={FIELD_CLASS}
              />
            </label>
          </>
        )}
        <div role="group" aria-label="Credits spent" className="flex flex-col gap-1 text-meta text-fg-secondary">
          Credits spent
          <div className="flex items-center gap-2">
            <input
              type="number"
              name="credits_min"
              aria-label="Credits spent from"
              min={0}
              step="any"
              value={creditsMinText}
              onChange={(e) => setCreditsMinText(e.target.value)}
              onBlur={snapCreditsMin}
              placeholder="from"
              className={`w-28 ${FIELD_CLASS}`}
            />
            <span aria-hidden="true">–</span>
            <input
              type="number"
              name="credits_max"
              aria-label="Credits spent to"
              min={0}
              step="any"
              value={creditsMaxText}
              onChange={(e) => setCreditsMaxText(e.target.value)}
              onBlur={snapCreditsMax}
              placeholder="to"
              className={`w-28 ${FIELD_CLASS}`}
            />
          </div>
        </div>
      </div>
      {input.trim().length > 0 && input.trim().length < MIN_QUERY_LENGTH && !isFullId && (
        <p className="mt-1 text-meta text-fg-hint">Type at least {MIN_QUERY_LENGTH} characters.</p>
      )}
      {subscription === 'paid_plan' && (
        <p className="mt-1 text-meta text-fg-hint">
          Includes plans activated by hand or gifted — only rows marked &ldquo;Paying&rdquo; have a
          live subscription.
        </p>
      )}

      {/* The result line (ClickUp 869f4c8yj): a page is 20 rows whichever
          filter is on, so without this the filter "looked like it does
          nothing". The numbers are the API's counts, never the page's. */}
      {data && !isError && (
        <p className="mt-4 text-meta text-fg-secondary" data-testid="users-result-line" role="status">
          {filtered ? (
            <>
              <span className="font-semibold text-fg-primary">{formatCount(total)}</span> of{' '}
              {formatCount(totalUnfiltered)} users · {filterLabels.join(' · ')}
            </>
          ) : (
            <>
              <span className="font-semibold text-fg-primary">{formatCount(total)}</span> users
            </>
          )}
          {query ? ` · matching “${query}”` : ''}
        </p>
      )}

      <div className="mt-2 overflow-x-auto rounded-card border border-border">
        <table className="w-full text-left text-body">
          <thead>
            <tr className="border-b border-border bg-surface-muted text-meta text-fg-secondary">
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Organization</th>
              <th className="px-3 py-2">Plan</th>
              <th className="px-3 py-2">Credits</th>
              <th className="px-3 py-2">Auth</th>
              <SortHeader column={SORT_COLUMNS.messages} sort={sort} dir={dir} onCycle={cycleSort} />
              <SortHeader column={SORT_COLUMNS.content} sort={sort} dir={dir} onCycle={cycleSort} />
              <SortHeader column={SORT_COLUMNS.credits_spent} sort={sort} dir={dir} onCycle={cycleSort} />
              <SortHeader column={SORT_COLUMNS.last_active} sort={sort} dir={dir} onCycle={cycleSort} />
              <SortHeader column={SORT_COLUMNS.last_generation} sort={sort} dir={dir} onCycle={cycleSort} />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={11} className="px-3 py-6 text-center text-meta text-fg-hint">
                  Loading…
                </td>
              </tr>
            )}
            {isError && !isLoading && (
              <tr>
                <td colSpan={11} className="px-3 py-6 text-center text-meta text-destructive">
                  Could not load users. Try again.
                </td>
              </tr>
            )}
            {!isLoading && !isError && users.length === 0 && (
              <tr>
                <td colSpan={11} className="px-3 py-6 text-center text-meta text-fg-hint">
                  {query || filtered ? 'No users match this search.' : 'No users found.'}
                </td>
              </tr>
            )}
            {!isLoading &&
              !isError &&
              users.map((u) => (
                <tr
                  key={u.id}
                  onClick={() => navigate(`/users/${u.id}`)}
                  className="cursor-pointer border-b border-border last:border-0 hover:bg-state-hover"
                >
                  <td className="px-3 py-2 text-fg-primary">{u.email ?? '—'}</td>
                  <td className="px-3 py-2 text-fg-secondary">{u.name ?? '—'}</td>
                  <td className="px-3 py-2 text-fg-secondary">{u.organization?.name ?? '—'}</td>
                  <td className="px-3 py-2 text-fg-secondary">
                    {u.plan?.name ?? '—'}
                    {u.paying && (
                      <span
                        title="A live subscription at the payment provider"
                        className="ml-2 inline-block rounded-full border border-success-border bg-success-soft px-2 py-0.5 text-micro font-medium text-success-soft-foreground"
                      >
                        Paying
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-fg-secondary">
                    {u.credits_remaining ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-fg-secondary">{u.auth_method ?? '—'}</td>
                  {/* `?? 0` / `?? null`: a bundle newer than the API gets rows
                      without the four activity fields and must not blank the page. */}
                  <td className="px-3 py-2 text-right tabular-nums text-fg-secondary">
                    {formatCount(u.messages_sent ?? 0)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-fg-secondary">
                    {formatCount(u.content_created ?? 0)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-fg-secondary">
                    {formatCredits(u.credits_spent ?? 0)}
                  </td>
                  <td className="px-3 py-2 text-fg-secondary">{formatDate(u.last_active ?? null)}</td>
                  <td className="px-3 py-2 text-fg-secondary">{formatDate(u.last_activity)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {total > 0 && (
        <div className="mt-3 flex items-center justify-between text-meta text-fg-secondary">
          <span>
            Page {page} of {totalPages} ({total} users)
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-card border border-border px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="rounded-card border border-border px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
